import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import SyriaMap from "@/components/SyriaMap"; // rebranded → generic RegionMap component (file kept for import stability)
import { Files, ShieldCheck, IdentificationCard, XCircle, QrCode, Warning, Clock, ChartLineUp, PaperPlaneTilt, WifiSlash } from "@phosphor-icons/react";

const Stat = ({ icon: Icon, label, value, tone = "default", testid }) => (
    <Card className="p-6 rounded-sm border border-border relative" data-testid={testid}>
        <div className="flex items-start justify-between">
            <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground font-bold">{label}</div>
                <div className="mt-3 text-4xl font-bold tracking-tight mono">{value ?? "—"}</div>
            </div>
            <div className={`h-10 w-10 rounded-sm flex items-center justify-center ${tone === "red" ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>
                <Icon size={20} weight="duotone" />
            </div>
        </div>
    </Card>
);

export default function Dashboard() {
    const { t } = useI18n();
    const { user } = useAuth();
    const nav = useNavigate();
    const [k, setK] = useState(null);
    const [cron, setCron] = useState(null);
    const isOfficer = ["enforcement_officer", "super_admin"].includes(user?.role);
    useEffect(() => {
        api.get("/reports/kpis").then((r) => setK(r.data)).catch(() => {});
        api.get("/reports/cron-health").then((r) => setCron(r.data)).catch(() => {});
    }, []);
    return (
        <div className="space-y-8" data-testid="dashboard-page">
            <div>
                <div className="text-[11px] tracking-[0.35em] uppercase text-primary">· {t(`role_${user?.role}`)}</div>
                <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1" data-testid="dashboard-welcome">
                    {t("welcome")}, {user?.full_name || user?.email}
                </h1>
                <p className="mt-1 text-sm text-muted-foreground">{t("tagline")}</p>
            </div>

            {isOfficer && (
                <Card className="p-6 sm:p-8 rounded-sm border-primary/40 bg-gradient-to-br from-primary/10 via-background to-background overflow-hidden relative" data-testid="officer-scanner-cta">
                    <div className="absolute -right-6 -top-6 opacity-10 pointer-events-none">
                        <QrCode size={180} weight="duotone" />
                    </div>
                    <div className="relative">
                        <div className="text-[10px] tracking-[0.35em] uppercase text-primary font-bold">· FIELD ENFORCEMENT</div>
                        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mt-1">Field Scanner</h2>
                        <p className="text-sm text-muted-foreground mt-2 max-w-lg">
                            Tap to open the camera and scan a windshield sticker. Works on any phone browser — no app install required.
                        </p>
                        <div className="mt-5 grid gap-2 sm:grid-cols-2 sm:max-w-lg">
                            <button
                                onClick={() => nav("/app/scanner")}
                                data-testid="officer-open-scanner"
                                className="h-14 rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground font-bold tracking-wider uppercase text-sm flex items-center justify-center gap-2 shadow-lg active:scale-95 transition-transform"
                            >
                                <QrCode size={22} weight="fill" />
                                Open Field Scanner
                            </button>
                            <button
                                onClick={() => nav("/offline")}
                                data-testid="officer-open-offline"
                                className="h-14 rounded-sm border border-primary/40 hover:border-primary hover:bg-primary/5 text-foreground font-bold tracking-wider uppercase text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform"
                            >
                                <WifiSlash size={20} weight="bold" />
                                Offline Mode
                            </button>
                        </div>
                        <div className="text-[10px] mono text-muted-foreground mt-3 tracking-wider">
                            Ed25519 signature verified · online or offline
                        </div>
                    </div>
                </Card>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat icon={Files} label={t("totalApps")} value={k?.total_applications} testid="kpi-total" />
                <Stat icon={Clock} label={t("pendingReview")} value={k?.pending_review} testid="kpi-pending" />
                <Stat icon={ShieldCheck} label={t("approvedCount")} value={k?.approved} testid="kpi-approved" />
                <Stat icon={IdentificationCard} label={t("activePlates")} value={k?.active_plates} testid="kpi-active" />
                <Stat icon={XCircle} label={t("revokedPlates")} value={k?.revoked_plates} tone="red" testid="kpi-revoked" />
                <Stat icon={QrCode} label={t("scans24h")} value={k?.scans_last_24h} testid="kpi-scans" />
                <Stat icon={Warning} label={t("fraudFlags")} value={k?.fraud_flags} tone="red" testid="kpi-fraud" />
            </div>

            <SyriaMap />

            <Card className="p-6 rounded-sm border-border" data-testid="cron-health-card">
                <div className="flex items-center justify-between mb-3">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-bold flex items-center gap-2">
                        <ChartLineUp size={14} weight="duotone" /> Renewal Sweep · Health
                    </div>
                    <div className="text-[10px] mono text-muted-foreground">runs nightly 08:00 UTC</div>
                </div>
                {cron?.last_run ? (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                        <div>
                            <div className="text-[10px] tracking-widest uppercase text-muted-foreground">Last run</div>
                            <div className="mono mt-1">{new Date(cron.last_run.ts).toLocaleString()}</div>
                        </div>
                        <div>
                            <div className="text-[10px] tracking-widest uppercase text-muted-foreground">Scanned</div>
                            <div className="mono mt-1 text-2xl font-bold" data-testid="cron-scanned">{cron.last_run.meta?.scanned ?? 0}</div>
                        </div>
                        <div>
                            <div className="text-[10px] tracking-widest uppercase text-muted-foreground">Sent</div>
                            <div className="mono mt-1 text-2xl font-bold text-green-700" data-testid="cron-sent">{cron.last_run.meta?.sent ?? 0}</div>
                        </div>
                        <div>
                            <div className="text-[10px] tracking-widest uppercase text-muted-foreground">Skipped</div>
                            <div className="mono mt-1 text-2xl font-bold text-amber-600" data-testid="cron-skipped">{cron.last_run.meta?.skipped ?? 0}</div>
                        </div>
                        <div className="col-span-2 sm:col-span-4 text-xs text-muted-foreground border-t border-border pt-3">
                            <PaperPlaneTilt size={12} className="inline me-1" /> {cron.upcoming_expiring_30d} plate(s) expire within the next 30 days.
                        </div>
                    </div>
                ) : (
                    <div className="text-sm text-muted-foreground">No run recorded yet. Cron will fire at 08:00 UTC.</div>
                )}
            </Card>

            <Card className="p-8 rounded-sm border-border">
                <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-bold mb-4">Security posture</div>
                <div className="grid sm:grid-cols-3 gap-6 text-sm">
                    <div className="flex items-start gap-3">
                        <div className="h-8 w-8 stripe-green rounded-sm shrink-0" />
                        <div><b>Ed25519</b> signing active · key rotation ready · public JWKS exposed</div>
                    </div>
                    <div className="flex items-start gap-3">
                        <div className="h-8 w-8 stripe-amber rounded-sm shrink-0" />
                        <div><b>MFA</b> optional per user · TOTP RFC-6238 compatible</div>
                    </div>
                    <div className="flex items-start gap-3">
                        <div className="h-8 w-8 stripe-red rounded-sm shrink-0" />
                        <div><b>Audit chain</b> tamper-evident SHA-256 hash chain across every mutation</div>
                    </div>
                </div>
            </Card>
        </div>
    );
}
